import styled from '@emotion/styled';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import React, { useEffect, useState } from 'react';
import { BsPersonCircle, BsImageAlt, BsBuildingsFill } from 'react-icons/bs';

import Camera from '../Icons/Camera.svg';
import './BayAvatar.scss';

type AvatarSize = 'xl' | 'lg' | 'md' | 'sm' | 'sm-6' | 'md-5';

interface BayAvatarProps {
	/** @deprecated Use `firstName` and `lastName` instead */
	first?: string;
	/** @deprecated Use `firstName` and `lastName` instead */
	last?: string;
	firstName?: string;
	lastName?: string;
	imgUrl?: string;
	size?: AvatarSize | string;
	bgColor?: string;
	editable?: boolean;
	imageAlt?: boolean;
	borderColor?: string;
	uploaderOverlayClass?: string;
	onClick?: () => void;
	isCompanyImage?: boolean;
	showFallbackIcon?: boolean;
	disableBorderRadius?: boolean;
}

// TODO: Refactor this to use scss modules
// The Scss module is in BayAvatar.scss We just need to import it and use the classes
const SvgIcon = styled.img``;

const BayAvatar = ({
	first,
	last,
	firstName,
	lastName,
	imgUrl = '',
	size = '',
	onClick,
	editable = false,
	bgColor,
	borderColor,
	imageAlt = false,
	uploaderOverlayClass = '',
	isCompanyImage = false,
	showFallbackIcon = false,
	disableBorderRadius = false,
}: BayAvatarProps) => {
	const [imageError, setImageError] = useState(false);

	useEffect(() => {
		setImageError(false);
	}, [imgUrl]);

	// NOTE: Backport outdated props
	const userFirstName = first || firstName;
	const userLastName = last || lastName;

	const userInitials = [userFirstName, userLastName]
		.filter((name) => name?.length > 0)
		.map((name) => name?.[0])
		.join('');

	const getAvatarSize = (avatarSize?: AvatarSize | string) => {
		switch (avatarSize) {
		case 'xl':
			return {
				className: imageAlt ? 'image-xl' : 'avatar-xl',
				bgSize: imageAlt ? '7em' : '6em',
				fontSize: 'fs-3',
			};

		case 'lg':
			return {
				className: imageAlt ? 'image-lg' : 'avatar-lg',
				bgSize: imageAlt ? '6em' : '5em',
				fontSize: 'fs-4',
			};

		case 'md':
			return {
				className: imageAlt ? 'image-md' : 'avatar-md',
				bgSize: imageAlt ? '4em' : '3em',
				fontSize: 'fs-6',
			};

		case 'md-5':
			return {
				className: imageAlt ? 'image-md' : 'avatar-md',
				bgSize: imageAlt ? '4em' : '3em',
				fontSize: 'fs-5',
			};

		case 'sm':
			return {
				className: imageAlt ? 'image-sm' : 'avatar-sm',
				bgSize: imageAlt ? '3em' : '2em',
				fontSize: 'fs-7',
			};

		case 'sm-6':
			return {
				className: imageAlt ? 'image-sm-6' : 'avatar-sm-6',
				bgSize: imageAlt ? '3.5em' : '2.5em',
				fontSize: 'fs-6',
			};

		default:
			return {
				className: imageAlt ? 'image' : 'avatar',
				bgSize: imageAlt ? '5em' : '4em',
				fontSize: 'fs-5',
			};
		}
	};

	const getImageSize = (imageSize?: AvatarSize | string) => {
		switch (imageSize) {
		case 'xl':
			return {
				className: 'bs-image-alt-xl',
				paddingSize: '16px 26px',
			};

		case 'lg':
			return {
				className: 'bs-image-alt-lg',
				paddingSize: '12px 22px',
			};

		case 'md':
			return {
				className: 'bs-image-alt-md',
				paddingSize: '8px 12px',
			};

		case 'sm':
			return {
				className: 'bs-image-alt-sm',
				paddingSize: '6px 10px',
			};

		case 'sm-6':
			return {
				className: 'bs-image-alt-sm-6',
				paddingSize: '6px 10px',
			};

		default:
			return {
				className: 'bs-image-alt',
				paddingSize: '10px 20px',
			};
		}
	};

	const companyImageDimensions = {
		width: '10.625rem',
		height: '8rem',
		imageSize: {
			width: '4.5rem',
			height: '3.375rem',
		},
	};

	const stylesObject: React.CSSProperties = {
		height: isCompanyImage ? companyImageDimensions.height : getAvatarSize(size)?.bgSize,
		width: imageAlt
			? isCompanyImage ? companyImageDimensions.width : `${parseInt(getAvatarSize(size).bgSize) + 1}em`
			: getAvatarSize(size).bgSize,
		// `backgroundImage`, NOT the `background` shorthand. The shorthand resets
		// every background longhand it does not name -- background-position to
		// `0% 0%`, background-size to `auto`, background-repeat to `repeat` -- and
		// as an inline style it outranks the stylesheet. `.avatar-image` sets
		// exactly those three (center / 105% / no-repeat), so the shorthand blanked
		// them and the avatar rendered at natural size, top-left, tiled.
		//
		// This only showed up when `!important` came off those three declarations
		// in the Aug 2026 sweep -- the keyword had been the one thing beating the
		// inline shorthand. The keyword was a symptom; this line was the cause.
		backgroundImage: `url(${imgUrl})`,
		border: `1px solid ${borderColor || 'transparent'}`,
	};

	const setImage = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
		const target = event.target as HTMLImageElement;
		target.onerror = null;
		setImageError(true);

		if (showFallbackIcon) {
			return;
		}

		target.src = `${window.location.origin}/global/images/empty-state/person-circle.svg`;
		return;
	};

	const renderFallbackIcon = () => (
		imageAlt ? (
			isCompanyImage || showFallbackIcon ?
				<BsBuildingsFill
					color="var(--bs-gray-300)"
					size="100%"
					style={{ padding: getImageSize(size).paddingSize }}
				/> :
				<BsImageAlt
					color="var(--bs-gray-300)"
					size="100%"
					style={{ padding: getImageSize(size).paddingSize }}
				/>
		) : (
			<BsPersonCircle color="var(--bs-gray-300)" size="100%" />
		)
	);

	//TODO: fix when imgUrl is null
	const checkBGColor = (url: string | undefined) => {
		const oBool =
			(isNil(url) &&
				isEmpty(url) &&
				((url?.length &&
					!url.startsWith('http') &&
					!url.startsWith('/') &&
					!url.startsWith('.')) ||
					url.search('nophoto') >= 0)) ||
			(((url?.length &&
				!url.startsWith('http') &&
				!url.startsWith('/') &&
				!url.startsWith('.')) ||
				url.search('nophoto') >= 0) &&
				userInitials?.length > 0) ||
			imageError;

		if (
			(userInitials?.length > 0 && oBool) ||
			oBool ||
			userInitials?.length <= 0
		) {
			return true;
		}
		return false;
	};

	return (
		<div
			className={`${imageAlt ? getImageSize(size).className : ''} ${getAvatarSize(size).className} ${disableBorderRadius ? 'rounded-0' : ''}`}
			style={{
				backgroundColor: bgColor
					? bgColor
					: checkBGColor(imgUrl)
						? 'var(--bs-gray-25)'
						: 'var(--bs-blue-200)',
				border: (imgUrl === '' || (imageError && showFallbackIcon)) && imageAlt ? isCompanyImage ? '6px solid var(--bs-gray-300)' : '2px solid var(--bs-gray-300)': '',
				width: isCompanyImage ? companyImageDimensions.width : undefined,
				height: isCompanyImage ? companyImageDimensions.height : undefined,
			}}
		>
			{editable && (
				<div
					className={`${imageAlt ? 'imageuploader align-items-center justify-content-center cursor-pointer' : 'uploader cursor-pointer'} ${uploaderOverlayClass}`}
					onClick={onClick}
				>
					<SvgIcon src={Camera} />
				</div>
			)}
			{imgUrl && imgUrl !== '' ? (
				(!imgUrl.startsWith('http') &&
					!imgUrl.startsWith('/') &&
					!imgUrl.startsWith('.') &&
					!imgUrl.startsWith('data:')) ||
				imgUrl.search('nophoto') >= 0 ? (
						imageAlt ? (
							<BsImageAlt
								color="var(--bs-gray-300)"
								size="100%"
								style={{ padding: getImageSize(size).paddingSize }}
							/>
						) : (
							<BsPersonCircle color="var(--bs-gray-300)" size="100%" />
						)
					) : (
						<>
							{!imageError && (
								<div
									className={`${imageAlt ? getImageSize(size).className : 'rounded-circle'} ${disableBorderRadius ? 'rounded-0' : ''} avatar-image`}
									style={stylesObject}
								/>
							)}
							{imageError && showFallbackIcon ? (
								renderFallbackIcon()
							) : (
								<img
									className={`avatar-image rounded-circle ${
										imageError ? '' : 'd-none'
									}`}
									src={imgUrl}
									style={{
										height: isCompanyImage ? companyImageDimensions.height : getAvatarSize(size)?.bgSize,
										width: isCompanyImage ? companyImageDimensions.width : getAvatarSize(size)?.bgSize,
									}}
									onError={(e) => setImage(e)}
									alt={`Avatar image for ${userFirstName} ${userLastName}`}
								/>
							)}
						</>
					)
			) : (
				<span
					className={`avatar-initials fw-semibold text-dark position-relative ${getAvatarSize(size)?.fontSize}`}
				>
					{!userInitials || userInitials.length === 0 ? (
						renderFallbackIcon()
					) : (
						userInitials.toUpperCase()
					)}
				</span>
			)}
		</div>
	);
};

export default BayAvatar;
